import React, { useState, useEffect, useCallback, useMemo } from "react";

interface CarouselProps {
  images: any[];
  interval?: number;
}

/**
 * Build a usable image URL. API may return:
 * - a string URL
 * - full URL in `url` / `images` / `file_path` (avoid `https://` + `https://...`)
 * - host-only path in `file_path` (prepend https://)
 */
const sliderImageSrc = (image: any): string => {
  if (typeof image === "string") {
    const s = image.trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith("//")) return `https:${s}`;
    return `https://${s}`;
  }
  if (image == null || typeof image !== "object") return "";

  const raw =
    image.file_path ??

    "";

  const s = String(raw).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  return `https://${s}`;
};

const Carousel: React.FC<CarouselProps> = ({ images, interval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const safeImages = useMemo(() => (Array.isArray(images) ? images : []), [images]);

  useEffect(() => {
    if (safeImages.length === 0) {
      setCurrentIndex(0);
      return;
    }
    setCurrentIndex((i) => (i >= safeImages.length ? 0 : i));
  }, [safeImages.length]);

  const goToPreviousSlide = useCallback(() => {
    if (safeImages.length <= 1 || isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? safeImages.length - 1 : prevIndex - 1
    );
  }, [safeImages.length, isAnimating]);

  const goToNextSlide = useCallback(() => {
    if (safeImages.length <= 1 || isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === safeImages.length - 1 ? 0 : prevIndex + 1
    );
  }, [safeImages.length, isAnimating]);

  useEffect(() => {
    if (!isAnimating) return;
    const t = setTimeout(() => setIsAnimating(false), 720);
    return () => clearTimeout(t);
  }, [isAnimating, currentIndex]);

  useEffect(() => {
    if (safeImages.length <= 1) return;
    const slideInterval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % safeImages.length);
    }, interval);
    return () => clearInterval(slideInterval);
  }, [safeImages.length, interval]);

  if (safeImages.length === 0) {
    return (
      <div
        className="mb-6 overflow-hidden min-h-[150px] h-[200px] md:h-[300px] relative flex items-center justify-center bg-gray-100 text-gray-500 text-sm"
        dir="ltr"
      >
        No banner images available
      </div>
    );
  }

  return (
    <div className="mb-6 overflow-hidden min-h-[150px] h-[200px] md:h-[300px] relative" dir="ltr">
      <div className="relative w-full h-full">
        <div
          className="flex transition-transform duration-700 ease-in-out w-full h-full"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {safeImages.map((image, index) => {
            const src = sliderImageSrc(image);
            const key = `slide-${index}-${src || String(index)}`;
            return (
              <img
                key={key}
                src={src || undefined}
                alt={`Slide ${index + 1}`}
                className="w-full h-full object-fill flex-shrink-0 min-w-full"
                referrerPolicy="no-referrer"
                loading={index === 0 ? "eager" : "lazy"}
              />
            );
          })}
        </div>

        {safeImages.length > 1 && (
          <div className="absolute z-30 flex bottom-5 left-1/2 transform -translate-x-1/2 space-x-3">
            {safeImages.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`w-3 h-3 rounded-full ${
                  index === currentIndex ? "bg-white" : "bg-gray-300"
                }`}
                aria-label={`Slide ${index + 1}`}
                onClick={() => {
                  if (!isAnimating) setCurrentIndex(index);
                }}
              />
            ))}
          </div>
        )}

        {safeImages.length > 1 && (
          <>
            <button
              type="button"
              className="absolute top-0 left-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
              onClick={goToPreviousSlide}
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 group-hover:bg-white/50">
                <svg
                  className="w-4 h-4 text-white"
                  aria-hidden="true"
                  fill="none"
                  viewBox="0 0 6 10"
                >
                  <path
                    d="M5 1L1 5l4 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="sr-only">Previous</span>
              </span>
            </button>

            <button
              type="button"
              className="absolute top-0 right-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
              onClick={goToNextSlide}
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 group-hover:bg-white/50">
                <svg
                  className="w-4 h-4 text-white"
                  aria-hidden="true"
                  fill="none"
                  viewBox="0 0 6 10"
                >
                  <path
                    d="M1 1l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="sr-only">Next</span>
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Carousel;
